import React, { useEffect } from 'react'
import { getResolvedColors } from '../utils/colorResolver'
import { isHrEnabled } from '../utils/hr'
import iconStar from '../assests/icon_star.png'
import iconHeart from '../assests/heart.png'
import './RecordDetailDialog.css'
import iconRat from "../assests/rat.png";
import iconOx from "../assests/ox.png";
import iconTiger from "../assests/tiger.png";
import iconRabbit from "../assests/rabbit.png";
import iconDragon from "../assests/dragon.png";
import iconSnake from "../assests/snake.png";
import iconHorse from "../assests/horse.png";
import iconGoat from "../assests/goat.png";
import iconMonkey from "../assests/monkey.png";
import iconRooster from "../assests/rooster.png";
import iconDog from "../assests/dog.png";
import iconPig from "../assests/pig.png";

interface HistoryRecord {
  id: string
  employeeName: string
  employeeId: string
  city: string
  cityName: string
  startDate: string
  endDate: string
  totalDays: number
  companyCompensation: number
  employeeCompensation?: number
  calculatedAt: string
  recordType?: string
  vacationData: {
    birthType?: string
    hasExtendedDays?: boolean
    rewardLeave?: boolean
    deliveryMethod?: string
    pregnancyDuration?: number
  }
  allowanceData: {
    monthlyAverageSalary?: number
    socialInsuranceBase?: number
    governmentSubsidy?: number
    [key: string]: unknown
  }
}

export interface VacationCalculation {
  employeeName: string
  employeeId: string
  city: string
  startDate: string
  birthType: 'single' | 'twins' | 'triplets' | 'quadruplets'
  hasExtendedDays: boolean
  rewardLeave: boolean
  deliveryMethod: 'normal' | 'difficult' | 'miscarriage'
  pregnancyDuration?: number
  isMiscarriage?: boolean
  calculatedDays: number
  totalAllowanceDays?: number // 享受津贴天数（用于津贴计算）
  maternityLeaveStartDate?: string // API返回的实际产假开始日期
  maternityLeaveEndDate?: string // API返回的实际产假结束日期
}

// 定义生肖图片接口
interface ZodiacImages {
  '鼠': string;
  '牛': string;
  '虎': string;
  '兔': string;
  '龙': string;
  '蛇': string;
  '马': string;
  '羊': string;
  '猴': string;
  '鸡': string;
  '狗': string;
  '猪': string;
  default: string;
}

interface RecordDetailDialogProps {
  record: HistoryRecord | null
  isOpen: boolean
  onClose: () => void
  onRecalculate?: (record: HistoryRecord) => void
}

const RecordDetailDialog: React.FC<RecordDetailDialogProps> = ({ record, isOpen, onClose, onRecalculate }) => {
  // 禁用背景页面滚动
  useEffect(() => {
    if (isOpen) {
      // 保存当前滚动位置
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'
    } else {
      // 恢复滚动
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1)
      }
    }

    return () => {
      // 清理函数：确保组件卸载时恢复滚动
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // 工具函数（用于页面展示与 PDF 导出）
  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('zh-CN')

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || amount === null) return '-'
    return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`
  }

  const getBirthTypeText = (birthType?: string) => {
    switch (birthType) {
      case 'single': return '单胎'
      case 'twins': return '双胞胎'
      case 'triplets': return '三胞胎及以上'
      default: return '单胎'
    }
  }

  const getDeliveryMethodText = (method?: string) => {
    switch (method) {
      case 'normal': return '顺产'
      case 'difficult': return '难产'
      case 'miscarriage': return '流产'
      default: return '顺产'
    }
  }

  const getCityDisplayName = (cityCode: string) => {
    const cityMap: { [key: string]: string } = {
      'BJ': '北京', 'FS': '佛山', 'GZ': '广州', 'SZ': '深圳', 'ZH': '珠海', 'WH': '武汉',
      'SZ2': '苏州（园区）', 'CD': '成都', 'CQ': '重庆', 'TJ': '天津', 'XM': '厦门',
      'SH': '上海', 'QD': '青岛', 'SX': '绍兴'
    }
    return cityMap[cityCode] || cityCode
  }

  // 导出功能
  const handleSavePDF = () => {
    if (!record) {
      alert('无法导出PDF：记录数据不存在');
      return;
    }

  const run = async () => {
    const [{ jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
    ]);

    // 主题适配 - 解析 CSS 变量为实际颜色值
    const prefersDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const isDark =
      document.documentElement.classList.contains("dark") || prefersDark;
    const colors = getResolvedColors();

    const employeeName = record?.employeeName || "";
    const employeeId = record?.employeeId || "";

    // 来自假期数据的字段
    const startDate = record?.startDate || "";
    const rewardOrExtended = record?.vacationData
      ? record?.vacationData.hasExtendedDays || record?.vacationData.rewardLeave
        ? "是"
        : "否"
      : "";
    const deliveryText = record?.vacationData?.deliveryMethod

    const isMiscarriage =
      record?.vacationData.deliveryMethod === "miscarriage" ||
      /流产/.test(deliveryText || "");

    const num = (v: unknown): number => {
      const n =
        typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : 0;
      return isFinite(n as number) ? (n as number) : 0;
    };

    const fmtCny = (v: unknown) => `¥${num(v).toLocaleString()}`;

    // 构建容器
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-10000px";
    container.style.top = "0";
    container.style.width = "794px";
    container.style.padding = "40px";
    container.style.background = colors.bg;
    container.style.color = colors.text;
    container.style.fontFamily =
      '"PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans SC", system-ui, -apple-system, sans-serif';
    container.style.lineHeight = "1.6";
    container.style.boxSizing = "border-box";

    // B 部分：津贴计算信息（严格对齐 AllowanceForm.tsx 的“津贴计算”分区）
    const formKVs: Array<[string, string]> = [];
    console.log('12112')
    if (record.allowanceData) {
      const pushMoney = (label: string, raw?: unknown) => {
        if (raw === undefined || raw === null) return;
        if (typeof raw === "string") {
          if (raw.trim() !== "") formKVs.push([label, fmtCny(raw)]);
        } else if (typeof raw === "number") {
          if (!Number.isNaN(raw)) formKVs.push([label, fmtCny(raw)]);
        }
      };

      // 使用宽松的只读视图以避免 any
      const fd = record.allowanceData as unknown as Readonly<Record<string, unknown>>;
      // 1) 政府发放金额总数
      pushMoney("政府发放金额总数", fd["governmentAmount"]);

      // 2) 单位申报的上年度月平均工资（有值时展示）
      pushMoney("单位申报的上年度月平均工资", fd["declaredSalary"]);

      // 3) 员工产前12个月的月平均工资
      pushMoney("员工产前12个月的月平均工资", fd["avgSalary12Months"]);

      // 4) 基本工资（根据是否跨四月展示不同字段）
      if (fd["crossApril"]) {
        pushMoney("调整前员工的基本工资", fd["monthlyBaseSalary"]);
        pushMoney("调整后员工的基本工资", fd["adjustedMonthlyBaseSalary"]);
      } else {
        pushMoney("员工的基本工资", fd["paidSalary"]);
      }
    }

    console.log('12112', formKVs)


    // 返还金额信息：来源于 AllowanceForm.tsx 的表单字段（仅展示填写的项目）
    // 增项（红）：employeeInsurance/supplementaryFund/personalIncomeTax/employerESPP/flexibleBenefit/customInputs
    // 减项（绿）：spotOn/otherDeductions/customDeductions
    const refundAdds: Array<[string, string]> = [];
    const refundSubs: Array<[string, string]> = [];
    if (record.allowanceData) {
      const pushAdd = (label: string, raw?: unknown) => {
        if (raw === undefined || raw === null) return;
        const v = typeof raw === "string" ? raw.trim() : raw;
        if (v === "" || (typeof v === "number" && Number.isNaN(v))) return;
        refundAdds.push([label, fmtCny(raw as unknown)]);
      };
      const pushSub = (label: string, raw?: unknown) => {
        if (raw === undefined || raw === null) return;
        const v = typeof raw === "string" ? raw.trim() : raw;
        if (v === "" || (typeof v === "number" && Number.isNaN(v))) return;
        refundSubs.push([label, fmtCny(raw as unknown)]);
      };

      const fd = record?.allowanceData as unknown as Readonly<Record<string, unknown>>;
     // 增项（对应页面默认输入项）
     const adjustedSocialInsuranceBase = Number(fd["adjustedSocialInsuranceBase"]) || 0;
     console.log('adjustedSocialInsuranceBase', adjustedSocialInsuranceBase) 
     if (adjustedSocialInsuranceBase > 0) {
       // 有调整后社保基数时显示调整前后的社保公积金
       pushAdd("调整前的个人社保公积金合计", fd["socialInsuranceBase"]);
       pushAdd("调整后的个人社保公积金合计", fd["adjustedSocialInsuranceBase"]);
     } else {
       pushAdd("月度个人部分社保公积金合计", fd["employeeInsurance"]);
     }
      pushAdd("月度补充公积金", fd["supplementaryFund"]);
      pushAdd("月度工会费", fd["personalIncomeTax"]);
      pushAdd("月度ESPP", fd["employerESPP"]);
      pushAdd("弹性福利自费", fd["flexibleBenefit"]);
      // 用户自定义增项
      type MaybeKV = { label?: string; value?: string };
      const customInputs = (fd["customInputs"] as MaybeKV[] | undefined) || [];
      customInputs.forEach((ci) => {
        const label = ci?.label?.trim();
        const value = ci?.value?.trim();
        if (label && value) refundAdds.push([label, fmtCny(value)]);
      });
      // 减项
      pushSub("SpotOn", fd["spotOn"]);
      pushSub("其他扣减", fd["otherDeductions"]);
      const customDeductions = (fd["customDeductions"] as MaybeKV[] | undefined) || [];
      customDeductions.forEach((cd) => {
        const label = cd?.label?.trim();
        const value = cd?.value?.trim();
        if (label && value) refundSubs.push([label, fmtCny(value)]);
      });
    }

    // C 返还金额信息（若缺失按0）
    const refundAmount = record?.allowanceData["employeeRefundAmount"] ?? 0;

    // D 计算结果（高亮） + 详情
    const display = record?.allowanceData
      ? {
          allowanceAmount: record?.allowanceData.maternityAllowance || 0,
          difference: record?.allowanceData.compensationAmount || 0,
          paidSalary: record?.allowanceData.paidMaternityWage || 0,
          governmentAmount: num(record?.allowanceData?.governmentAmount),
          totalAllowanceDays: record?.allowanceData.allowanceDays || 0,
          employeeRefundAmount: refundAmount || 0,
          breakdown: Array.isArray(
            record?.allowanceData.allowanceCompensationDetails,
          )
            ? (record?.allowanceData.allowanceCompensationDetails as string[])
            : typeof record?.allowanceData.allowanceCompensationDetails === "string"
              ? [record?.allowanceData.allowanceCompensationDetails]
              : record?.allowanceData?.breakdown || [],
        }
      : {
          allowanceAmount: record?.allowanceData?.allowanceAmount || 0,
          difference: record?.allowanceData?.difference || 0,
          paidSalary: record?.allowanceData?.paidSalary || 0,
          governmentAmount: record?.allowanceData?.governmentAmount || 0,
          totalAllowanceDays: record?.allowanceData?.totalAllowanceDays || 0,
          employeeRefundAmount: record?.allowanceData?.employeeRefundAmount || 0,
          breakdown: record?.allowanceData?.breakdown || [],
        };

    // 退款详情（用来决定是否展示返还金额详情模块）
    const refundDetailLines: string[] = (() => {
      const src = record?.allowanceData?.refundDetails;
      if (!src) return [];
      if (Array.isArray(src)) return src.filter(Boolean).map(String);
      return [String(src)];
    })();

    // 安全读取妊娠时长（避免 any）
    const pregnancyWeeks: number | undefined = (() => {
      if (!record.vacationData) return undefined;
      const v = (record.vacationData as unknown as Record<string, unknown>)[
        "pregnancyDuration"
      ];
      return typeof v === "number" ? v : undefined;
    })();


    function getZodiacImage() {
      const zodiacImages = {
        '鼠': iconRat,
        '牛': iconOx,
        '虎': iconTiger,
        '兔': iconRabbit,
        '龙': iconDragon,
        '蛇': iconSnake,
        '马': iconHorse,
        '羊': iconGoat,
        '猴': iconMonkey,
        '鸡': iconRooster,
        '狗': iconDog,
        '猪': iconPig,
        default: iconHeart // 默认图片
    };
    const zodiacs: Array<keyof ZodiacImages> = [
      '鼠', '牛', '虎', '兔', '龙', '蛇', 
      '马', '羊', '猴', '鸡', '狗', '猪'
  ];

      
      const date = new Date(startDate);
      // 检查日期是否有效
      if (isNaN(date.getTime())) {
          throw new Error(`无效的日期格式: ${startDate}`);
      }
      if (isMiscarriage) {
        return zodiacImages.default;
      }
      
      const year = date.getFullYear();
      
      // 计算生肖（1900年是鼠年）
      const index = (year - 1900) % 12;
      const zodiac = zodiacs[(index + 12) % 12]; // 确保索引为正数
      
      return zodiacImages[zodiac] || zodiacImages.default;

  }    // 2) Define your texts
  const titleForMiscarriage = '欢迎回来，愿你安然向前';
  const tipForMiscarriage = '愿你被温柔以待，也被自己温柔以待';
  
  // Keep your current defaults for non-流产
  const titleDefault = '欢迎回来，新妈妈！';
  const tipDefault = '恭喜您开启人生新篇章！我们为您准备了这份津贴测算单，愿您在新角色中收获满满幸福，健康平安每一天。';
  
   const pdfTitle  = isMiscarriage ? titleForMiscarriage : titleDefault;
  const pdfEl= isMiscarriage ? tipForMiscarriage : tipDefault;
   
  // 定义文案
const footerDefault = '愿您和宝宝的生活充满阳光与欢笑。HR 部门 敬上';
const footerForMiscarriage = '愿你被温柔以待，愿你安然向前。HR 部门 敬上';
const footerSub = isMiscarriage ? footerForMiscarriage : footerDefault;

    
    // theme-check-enable

    // 六块内容
    container.innerHTML = `
        <div class="pdf-root theme-warm">
  <style>
      :root {
          /* Base Colors */
          }

      /* Warm Theme (Default) */
      .theme-warm {
          --font-family: 'Kaiti SC', 'STKaiti', 'KaiTi', 'SimSun', serif, "PingFang SC", "Microsoft YaHei";
      }
      @keyframes flow-background {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
      }
      .pdf-root {
          font-family: var(--font-family);
          color: var(--text-dark);
          padding: 20px;
          background: var(--theme-bg);
          background-image: var(--theme-bg-animation);
          animation: flow-background 20s ease infinite;
          background-size: 400% 400%;
      }
      .pdf-root .container {
          max-width: 794px;
          margin: 0 auto;
          background-color: var(--white);
          background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Cg fill-rule="evenodd"%3E%3Cg fill="%23fce9e9" fill-opacity="0.4"%3E%3Cpath opacity=".5" d="M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z"/%3E%3Cpath d="M6 5V0h1v5h5v1H6v5H5V6H0V5h5z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');
          padding: 40px;
          box-shadow: 0 10px 30px var(--container-shadow);
          border-radius: 16px;
          border: 1px solid var(--border-color);
          position: relative;
          overflow: hidden;
      }
      .pdf-root .container::before, .pdf-root .container::after {
          content: '';
          position: absolute;
          width: 150px;
          height: 150px;
          background: radial-gradient(circle, var(--soft-red), transparent);
          opacity: 0.2;
          border-radius: 50%;
          filter: blur(30px);
          z-index: 0;
      }
      .pdf-root .container::before { top: -50px; left: -50px; }
      .pdf-root .container::after { bottom: -50px; right: -50px; }
      .pdf-root .pdf-title {
          text-align:center;
          font-size:32px;
          margin:0;
          color: var(--ocbc-red); /* Changed: Title color to white for readability */
          font-weight:700;
      }
      .pdf-root .pdf-meta { display:flex; align-items:flex-end; justify-content:space-between; margin-top:4px; margin-bottom:16px; }
      .pdf-root .pdf-time { font-size:14px; color: var(--text-light); }
      .pdf-root .pdf-slogan { font-size:18px; color: var(--text-light); }
      .pdf-root .paper { border-radius: 12px; background: #fff; }
      .pdf-root .title-section { background: transparent; border: none; border-radius: 12px; padding: 20px; margin-bottom: 8px; }
      .pdf-root .ribbon { display:flex; align-items:center; justify-content:center; gap:10px;
      padding:12px 16px; 
      border-radius:12px; 
      margin-bottom:12px; text-shadow: 1px 1px 2px var(--ribbon-shadow); box-shadow: 0 4px 15px var(--container-shadow); }
      .pdf-root .ribbon .emoji { font-size:20px; line-height:1; }
      .pdf-root .section { border:1px dashed var(--soft-red); border-radius:16px; padding:16px 18px; margin:8px 0; background: var(--section-background); backdrop-filter: blur(5px); position: relative; overflow: hidden; transition: all 0.3s ease-in-out; }
      .pdf-root .section:hover { transform: translateY(-3px); box-shadow: 0 4px 15px var(--section-shadow-hover); }
      .pdf-root .section--noborder { border:none; background:transparent; padding:0; margin:0 0 8px; }
      .pdf-root .section h3 { margin:0 0 12px; font-size:18px; color: var(--ocbc-red); display: flex; align-items: center; font-weight: 600; }
      .pdf-root .kv { width:100%; border-collapse:collapse; font-size:15px; color: var(--text-dark); }
      .pdf-root .kv tr:nth-child(odd) { background: var(--bg-light); }
      .pdf-root .kv th, .pdf-root .kv td { text-align:left; padding:10px 12px; border-bottom:1px solid var(--border-color); white-space: nowrap; }
      .pdf-root .kv th { width:32%; color: var(--text-light); font-weight:600; }
      .pdf-root .kv.kv2 th { width:20%; }
      .pdf-root .summary-grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:12px; }
      .pdf-root .summary-notes {
          margin-top: 16px;
          font-size: 14px;
          color: var(--text-light);
          padding: 0 10px;
      }
      .pdf-root .summary-notes p {
          margin: 4px 0;
          line-height: 1.6;
      }
      .pdf-root .summary-card { background: var(--bg-light); border: 1px solid var(--border-color); border-radius: 12px; padding: 16px; text-align: center; transition: all 0.3s ease; }
      .pdf-root .summary-card:hover { transform: scale(1.05); box-shadow: 0 8px 25px var(--card-hover); border-color: var(--soft-red); }
      .pdf-root .summary-label { display: block; font-size: 16px; color: var(--text-light); margin-bottom: 8px; }
      .pdf-root .summary-value { display: block; font-size: 22px; color: var(--accent-color); font-weight: 700; transition: color 0.3s ease; }
      .pdf-root .summary-card:hover .summary-value { color: var(--gold-accent); }
      .pdf-root .accent { color: var(--ocbc-red); font-weight:700; font-size: 16px; }
      .pdf-root .mini { font-size:14px; color: var(--text-light); }
      .pdf-root .list { margin:8px 0; padding-left:18px; color: var(--text-dark); }
      .pdf-root .list li { margin:6px 0; line-height:1.7; }
      .pdf-root .formula-box { padding:10px 12px; border:1px dashed var(--soft-red); border-radius:8px; background: var(--bg-light); }
      .pdf-root .formula-box p { margin: 0; }
      .pdf-root .formula-step h5 { margin: 8px 0 4px; }
      .pdf-root .green-card { padding:14px 14px; border-radius:10px; border:1px solid var(--soft-red); background: linear-gradient(135deg, var(--bg-light), #fff); }
      .pdf-root .green-item { display:flex; align-items:center; justify-content:flex-start; padding:10px 12px; border-bottom:1px dashed var(--border-color); gap:8px; }
      .pdf-root .green-item:last-child { border-bottom:none; }
      .pdf-root .green-icon { width:14px; height:14px; display:inline-block; }

      .pdf-root .green-value { color: var(--text-dark); font-weight:normal; white-space:nowrap; text-align:left; }
      .pdf-root .plus { color: var(--plus-color); font-weight:600; }
      .pdf-root .minus { color: var(--minus-color); font-weight:600; }
      .pdf-root .note { margin-top:10px; padding:10px 12px; border:1px dashed var(--soft-red); border-radius:8px; background: var(--bg-light); color: var(--text-light); font-size:14px; }
      .pdf-root .footer { text-align:center; padding:24px 10px 8px; background: transparent; border:none; }
      .pdf-root .footer-line { height:1px; width:180px; margin:0 auto; background: var(--separator-gradient); border-radius:1px; }
      .pdf-root .footer-title { margin-top:18px; font-size:19px; color:var(--ocbc-red); font-weight:700; }
      .pdf-root .footer-sub { margin-top:8px; font-size:15px; color:var(--text-light); }
      .pdf-root .final-note-section {
          text-align: center;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid var(--border-color);
      }
      .pdf-root .icon-img {
          width: 60px;
          height: 70px;
      }
      .pdf-root .icon-img:last-child {
          transform: scaleX(-1);
      }
      @media print {
          @page {
              size: A4;
              margin: 10mm;
          }
          .pdf-root {
              margin: 0;
          }
          .pdf-root .container {
              box-shadow: none !important;
              border: none !important;
          }
          .pdf-page-break {
              page-break-before: always;
          }
          .pdf-root .ribbon {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              box-shadow: none !important;
          }
          .pdf-root .final-note-section .note {
              white-space: nowrap;
          }
      }
  </style>
<div class="container">
  <div class="paper">
      <section class="section section--noborder title-section">
          <div class="ribbon">
          <img src="${getZodiacImage()}" class="icon-img"></img>
        <div class="pdf-title">${pdfTitle}</div>
            <img src="${getZodiacImage()}" class="icon-img"></img>
            </div>
            <div class="pdf-slogan" style="text-align: center; color: var(--text-light); margin-top: 12px;">
            ${pdfEl}
            </div>
            </section>
  </div>

  <!-- Section A: Basic Info -->
  <section class="section">
      <h3>🍼 您的基本信息</h3>
      <table class="kv kv2"><tbody>
      ${(() => {
        const items: Array<[string, string]> = [];
        items.push(["城市", record.cityName]);
        items.push(["员工姓名", record.employeeName || '-']);
        items.push(["员工工号", record.employeeId || '-']);
        items.push(["产假开始日期", formatDate(record.startDate)]);
        items.push(["产假结束日期", formatDate(record.endDate)]);

        if (!isMiscarriage) items.push(["几胞胎", getBirthTypeText(record.vacationData?.birthType)]);
        if (!isMiscarriage && rewardOrExtended)
          items.push(["晚育/生育/奖励假", rewardOrExtended]);
        if (deliveryText && !isMiscarriage) items.push(["生育方式", getDeliveryMethodText(record.vacationData?.deliveryMethod)]);
        if (
          isMiscarriage &&
          typeof pregnancyWeeks === "number" &&
          pregnancyWeeks > 0
        ) {
          items.push(["妊娠时长（周）", `${pregnancyWeeks} 周`]);
        }
        let html = "";
        for (let i = 0; i < items.length; i += 2) {
          const [k1, v1] = items[i];
          const pair2 = items[i + 1];
          if (pair2) {
            const [k2, v2] = pair2;
            html += `<tr><th>${k1}</th><td>${v1}</td><th>${k2}</th><td>${v2}</td></tr>`;
          } else {
            html += `<tr><th>${k1}</th><td colspan="3">${v1}</td></tr>`;
          }
        }
        return html;
      })()}
      </tbody></table>
  </section>

  <!-- Section D: Core Results -->
  <section class="section">
      <h3>✨ 给您的幸福小结</h3>
      <div class="summary-grid">
          <div class="summary-card">
              <span class="summary-label">津贴天数</span>
              <span class="summary-value">${record.totalDays || 0} 天</span>
          </div>
          <div class="summary-card">
              <span class="summary-label">生育津贴补差</span>
              <span class="summary-value">${fmtCny(record.allowanceData.difference)}</span>
          </div>
          <div class="summary-card">
              <span class="summary-label">应享受津贴</span>
              <span class="summary-value">${fmtCny(record.allowanceData.monthlyAverageSalary)}</span>
          </div>
           <div class="summary-card">
              <span class="summary-label">${Number(record.employeeCompensation) > 0 ? '员工需返还金额' : '产假期间发放工资'}</span>
              <span class="summary-value">${Number(record.employeeCompensation) > 0 ? fmtCny(record.employeeCompensation) : fmtCny(record.allowanceData.paidMaternityWage)}</span>
          </div>
      </div>
      <div class="summary-notes">
            <p><strong>* 生育津贴补差：</strong>指当政府发放的生育津贴低于员工产假前平均工资时，由公司为员工补足的差额部分。</p>
            <p><strong>* ${Number(record.employeeCompensation) > 0 ? '员工需返还金额' : '产假期间发放工资'}：</strong>
            ${Number(record.employeeCompensation) > 0 ? '指产假期间公司为员工垫付的个人社保、公积金等费用，返工后需归还公司。' : '指员工依法休产假期间，用人单位按制度和合同约定持续支付的工资性收入。'}</p>
        </div>
  </section>

  <!-- Footer -->
  <section class="section footer section--noborder">
      <div class="footer-line"></div>
      <div class="footer-title">欢迎您重返工作岗位！</div>
      <div class="footer-sub">${footerSub}</div>
  </section>

  <div class="pdf-page-break" style="margin: 20px 0;">
      <svg width="100%" height="40" viewBox="0 0 100 100" preserveAspectRatio="none"><path d="M0,50 Q25,20 50,50 T100,50" stroke="var(--border-color-light)" stroke-width="2" fill="none"></path></svg>
  </div>
  <h4 style="text-align: center; color: var(--text-light); font-weight: normal; margin-bottom: 20px;">-- 详细信息供您参考 --</h4>

  <!-- Section B: Calculation Info -->
  <section class="section">
      <h3>💰 津贴计算信息</h3>
      <table class="kv"><tbody>
          ${formKVs.map(([k, v]) => `<tr><th>${k}</th><td>${v}</td></tr>`).join("")}
      </tbody></table>
  </section>

  <!-- Section C: Refund Info -->
   ${
    ['TJ', 'SX', 'XM', 'SH', 'QD'].includes(record.city) && (
        refundAdds.length > 0 ||
        refundSubs.length > 0 ||
        Number(display.employeeRefundAmount) > 0)
          ? `
  <section class="section">
      <h3>📝 公司垫付金额信息</h3>
      <table class="kv"><tbody>
         <tr><th>产假提交时间</th><td class="plus">${record.allowanceData.maternitySubmissionDate || ""}</td></tr>
          </tbody></table>
      <div class="mini">公司垫付</div>
      <table class="kv"><tbody>
       ${refundAdds.length > 0 ? `<tr><th colspan="2" class="plus">增项</th></tr>` : ""}
          ${refundAdds.map(([k, v]) => `<tr><th>${k}</th><td class="plus">${v}</td></tr>`).join("")}
          ${refundSubs.length > 0 ? `<tr><th colspan="2" class="minus">减项</th></tr>` : ""}
          ${refundSubs.map(([k, v]) => `<tr><th>${k}</th><td class="minus">${v}</td></tr>`).join("")}
      </tbody></table>
  </section>`
  : ""
}


  <!-- Section E: Calculation Details -->
  ${
    Array.isArray(display.breakdown) && display.breakdown.length > 0
      ? `
  <section class="section">
      <h3>🧮 公司补差金额计算详情</h3>
      <div class="green-card">
          ${(display.breakdown as string[])
            .map(
              (line) => `
            <div class="green-item"> 
              <div class="green-label"><img src="${iconStar}" class="green-icon" alt="*" /></div>
              <div class="green-value">${line}</div>
            </div>
          `,
            )
            .join("")}
      </div>
  </section>`
  : ""
}
${
  refundDetailLines.length > 0
    ? `
    <section class="section">
      <h3>🧮 员工返还金额计算详情</h3>
      <div class="green-card">
         ${refundDetailLines
            .map(
              (line) => `
            <div class="green-item"> 
              <div class="green-label"><img src="${iconStar}" class="green-icon" alt="*" /></div>
              <div class="green-value">${line}</div>
            </div>
          `,
            )
            .join("")}
      </div>
  </section>`
  : ""
}

  <!-- Section F: Policy -->
  <section class="section">
      <h3>💡 政策小贴士</h3>
      <ul class="list">
          <li><strong>计算原则：</strong>生育津贴应按产前12个月平均工资计算</li>
          <li><strong>补差条件：</strong>当计算出的津贴高于政府发放金额时需要补差</li>
          <li><strong>补差责任：</strong>用人单位负责补足差额部分</li>
          <li><strong>发放时间：</strong>应在收到政府津贴后及时补差</li>
          <li><strong>税务处理：</strong>补差部分按工资薪金所得计税</li>
          <li><strong>记录保存：</strong>相关计算和发放记录应妥善保存</li>
      </ul>
  </section>

  <!-- Section G: Formula -->
  <section class="section" style="margin-top: 0;">
      <h3>📚 计算方式参考</h3>
       ${(() => {
          const cityCode = (record?.city || "").toString();
          const isSH = cityCode === "SH" || /上海/.test(cityCode);
          const isSZ = cityCode === "SZ" || /深圳/.test(cityCode);
          const step1Common = `
            <div class="formula-step">
              <h5>第一步：计算各项基数</h5>
              <div class="formula-box">
                ${isSH ? `<p><strong>A = 单位申报的上年度月平均工资 ÷ 30 × 享受生育津贴天数</strong></p>` : ""}
                <p><strong>B = 员工产前12个月月均工资 ÷ 30 × 享受生育津贴天数</strong></p>
                <p><strong>C = 政府发放补贴金额</strong></p>
                ${isSZ ? `<p><strong>A = 产假期间领到工资总数</strong></p>` : ""}
              </div>
            </div>`;
          if (isSZ) {
            return `
              ${step1Common}
              <div class="formula-step">
                <h5>第二步：确定员工应享受补贴</h5>
                <div class="formula-box other-cities-formula">
                  <p><strong>D = MAX(B, C)</strong></p>
                </div>
              </div>
              <div class="formula-step">
                <h5>第三步：计算补差结果</h5>
                <div class="formula-box result-formula">
                  <p><strong>补差金额 = D - A</strong></p>
                </div>
              </div>
            `;
          }
          // 其他城市（含上海独有规则）
          return `
            ${step1Common}
            <div class="formula-step">
              <h5>第二步：确定员工应享受补贴</h5>
              ${
                isSH
                  ? `<div class="formula-box shanghai-formula"><p><strong>D = MAX(A, B, C)</strong></p></div>`
                  : `<div class="formula-box other-cities-formula"><p><strong>D = MAX(B, C)</strong></p></div>`
              }
                  </div>
            <div class="formula-step">
              <h5>第三步：计算补差结果</h5>
              <div class="formula-box result-formula">
                <p><strong>补差金额 = D - C</strong></p>
              </div>
            </div>
            ${
              ["TJ", "SX", "XM", "SH", "QD"].includes(cityCode)
                ? `
            <div class="formula-step">
              <h5>第四步：返工时需返还金额</h5>
              <div class="formula-box return-formula">
                <p><strong>返还金额 = 员工五险一金 + 补充公积金${cityCode === "SH" ? "（仅上海）" : ""} + 个人工会费 + ESPP</strong></p>
              </div>
            </div>`
                : ""
            }
          `;
        })()}
  </section>
  <!-- Final Note -->
  <div class="final-note-section" style="margin-top: 15px;">
      <div class="note">如对本结果有疑问或遇到特殊情形，请联系 HR，我们将第一时间为您提供帮助与说明。</div>
      <div class="pdf-time" style="margin-top: 4px;">报告生成于：2025-09-24 10:00:00</div>
  </div>
</div>
</div>`    

    document.body.appendChild(container);

    try {
      // 智能混排分页：尽量将多个小模块放同一页，空间不足时整体换页；仅当模块高度大于可用高度时允许在模块内部分页
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const headerSpaceMm = 10; // 顶部页眉空间
      const sectionGapMm = 2; // 模块之间的间距（进一步调小）
      const footerSpaceMm = 12; // 底部留白
      const sideMarginMm = 12; // 左右页边距
      const contentWidthMm = Math.max(0, pdfW - sideMarginMm * 2);
      const contentHeightMm = Math.max(
        0,
        pdfH - headerSpaceMm - footerSpaceMm,
      );

      const sections = Array.from(
        container.querySelectorAll(".section"),
      ) as HTMLElement[];

      // 预渲染每个模块为 Canvas，并计算其在 PDF 中的渲染高度
      const rendered = [] as {
        canvas: HTMLCanvasElement;
        imgW: number;
        imgH: number;
        renderHeightMm: number;
      }[];
      // 使用宽松的选项对象以避免第三方类型不兼容导致的报错
      type CanvasOpts = { [key: string]: unknown };
      for (const sec of sections) {
        const opts: CanvasOpts = {
          scale: 2,
          useCORS: true,
          backgroundColor: colors.bg,
        };
        const c = await html2canvas(
          sec,
          opts as unknown as Parameters<typeof html2canvas>[1],
        );
        const imgW = c.width;
        const imgH = c.height;
        const mmPerPx =
          contentHeightMm /
          Math.floor((imgW * contentHeightMm) / contentWidthMm);
        const renderHeightMm = imgH * mmPerPx;
        rendered.push({ canvas: c, imgW, imgH, renderHeightMm });
      }

      // 计算总页数（近似）
      let tempRemain = contentHeightMm;
      let totalPages = 1;
      for (const r of rendered) {
        const h = r.renderHeightMm;
        const needsSplit = h > contentHeightMm;
        if (needsSplit) {
          const pagesForSec = Math.ceil(h / contentHeightMm);
          totalPages += pagesForSec - 1;
          tempRemain = contentHeightMm - (h % contentHeightMm);
          if (tempRemain === contentHeightMm) tempRemain = 0;
        } else if (h <= tempRemain) {
          tempRemain -= h + sectionGapMm;
        } else {
          totalPages += 1;
          tempRemain = contentHeightMm - h - sectionGapMm;
        }
        if (tempRemain < 0) tempRemain = 0;
      }

      let pageIndex = 0;
      let yOffsetMm = headerSpaceMm; // 当前页内容起始 Y

      for (const r of rendered) {
        const mmPerPx =
          contentHeightMm /
          Math.floor((r.imgW * contentHeightMm) / contentWidthMm);
        const sectionHeightMm = r.imgH * mmPerPx;
        const needsSplit = sectionHeightMm > contentHeightMm;

        const ensureNewPage = () => {
          if (pageIndex > 0) pdf.addPage();
          // 去除页眉，仅保留顶部边距
          yOffsetMm = headerSpaceMm;
        };

        const drawFooter = () => {
          pdf.setFontSize(9);
          pdf.setTextColor(isDark ? 190 : 120);
          const day = new Date().toISOString().slice(0, 10);
          const footer = `${day} • HR Maternity System • Page ${pageIndex + 1} / ${totalPages}`;
          pdf.text(footer, pdfW / 2, pdfH - 8, { align: "center" as const });
        };

        // 若模块可放入当前页剩余空间，则直接放；否则换新页再放
        const remainMm = headerSpaceMm + contentHeightMm - yOffsetMm;
        if (!needsSplit && sectionHeightMm <= remainMm) {
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = r.imgW;
          pageCanvas.height = r.imgH;
          const ctx = pageCanvas.getContext("2d");
          ctx?.drawImage(r.canvas, 0, 0);
          const imgData = pageCanvas.toDataURL("image/png", 1.0);
          // 首页不绘制任何页眉
          pdf.addImage(
            imgData,
            "PNG",
            sideMarginMm,
            yOffsetMm,
            contentWidthMm,
            sectionHeightMm,
          );
          yOffsetMm += sectionHeightMm + sectionGapMm;
          // 若内容将近底部，提前画脚注并开启新页
          if (yOffsetMm > headerSpaceMm + contentHeightMm - 2) {
            drawFooter();
            pageIndex += 1;
            ensureNewPage();
          }
          continue;
        }

        // 否则换新页整体放置；若模块高度超过单页，则在模块内部分页
        if (sectionHeightMm <= contentHeightMm) {
          // 换页整体放置
          if (!(pageIndex === 0 && yOffsetMm === headerSpaceMm)) {
            drawFooter();
            pageIndex += 1;
            ensureNewPage();
          } else {
            // 第1页但空间不足，直接新页
            ensureNewPage();
          }
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = r.imgW;
          pageCanvas.height = r.imgH;
          const ctx = pageCanvas.getContext("2d");
          ctx?.drawImage(r.canvas, 0, 0);
          const imgData = pageCanvas.toDataURL("image/png", 1.0);
          pdf.addImage(
            imgData,
            "PNG",
            sideMarginMm,
            yOffsetMm,
            contentWidthMm,
            sectionHeightMm,
          );
          yOffsetMm += sectionHeightMm + sectionGapMm;
          continue;
        }

        // 模块高度超过一页：在模块内部分页渲染
        if (!(pageIndex === 0 && yOffsetMm === headerSpaceMm)) {
          drawFooter();
          pageIndex += 1;
          ensureNewPage();
        } else {
          ensureNewPage();
        }

        const pageHeightPx = Math.floor(
          (r.imgW * contentHeightMm) / contentWidthMm,
        );
        let offsetPx = 0;
        while (offsetPx < r.imgH) {
          const sliceHeightPx = Math.min(pageHeightPx, r.imgH - offsetPx);
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = r.imgW;
          sliceCanvas.height = sliceHeightPx;
          const sctx = sliceCanvas.getContext("2d");
          sctx?.drawImage(
            r.canvas,
            0,
            offsetPx,
            r.imgW,
            sliceHeightPx,
            0,
            0,
            r.imgW,
            sliceHeightPx,
          );
          const imgData = sliceCanvas.toDataURL("image/png", 1.0);
          const renderHMm = (sliceHeightPx / pageHeightPx) * contentHeightMm;
          pdf.addImage(
            imgData,
            "PNG",
            sideMarginMm,
            headerSpaceMm,
            contentWidthMm,
            renderHMm,
          );
          drawFooter();
          offsetPx += sliceHeightPx;
          if (offsetPx < r.imgH) {
            pageIndex += 1;
            ensureNewPage();
          }
        }
        yOffsetMm = headerSpaceMm + sectionGapMm; // 下一块从新页顶部间距后开始
      }

      const fileName =
        `生育津贴计算_${employeeName || "员工"}_${employeeId || ""}_${new Date().toISOString().slice(0, 10)}.pdf`.replace(
          /\s+/g,
          "_",
        );
      pdf.save(fileName);
    } catch (err) {
      console.error("导出PDF失败:", err);
      alert("导出PDF失败，请稍后重试");
    } finally {
      document.body.removeChild(container);
      // setShowExportMenu(false);
    }
  };

  void run();
  };

  if (!isOpen || !record) return null

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        {/* 固定头部 */}
        <div className="dialog-header">
          <h3>
            <i className="fas fa-file-alt" />
            历史记录详情
          </h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>

        {/* 统一滚动容器 */}
        <div className="dialog-body">
          {/* 员工信息 */}
          <section className="info-section">
            <div className="section-header">
              <i className="fas fa-user" />
              <h4>员工信息</h4>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <label>员工姓名</label>
                <span>{record.employeeName}</span>
              </div>
              <div className="info-item">
                <label>员工工号</label>
                <span>{record.employeeId}</span>
              </div>
              <div className="info-item">
                <label>所在城市</label>
                <span>{getCityDisplayName(record.city)}</span>
              </div>
            </div>
          </section>

          {/* 假期数据 */}
          <section className="info-section">
            <div className="section-header">
              <i className="fas fa-calendar-alt" />
              <h4>假期数据</h4>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <label>产假开始日期</label>
                <span>{formatDate(record.startDate)}</span>
              </div>
              <div className="info-item">
                <label>产假结束日期</label>
                <span>{formatDate(record.endDate)}</span>
              </div>
              <div className="info-item">
                <label>产假总天数</label>
                <span className="highlight">{record.totalDays}天</span>
              </div>
              {record.vacationData?.deliveryMethod !== 'miscarriage' && (
                <div className="info-item">
                <label>生育类型</label>
                <span>{getBirthTypeText(record.vacationData?.birthType)}</span>
              </div>
              )
                }
              
              <div className="info-item">
                <label>生育方式</label>
                <span>{getDeliveryMethodText(record.vacationData?.deliveryMethod)}</span>
              </div>
              {record.vacationData?.deliveryMethod !== 'miscarriage' && (
                <div className="info-item">
                <label>晚育/生育/奖励假</label>
                <span className={record.vacationData?.hasExtendedDays ? 'status-yes' : 'status-no'}>
                  {record.vacationData?.hasExtendedDays ? '是' : '否'}
                </span>
              </div>
              )
                }
              
              {record.vacationData?.pregnancyDuration && (
                <div className="info-item">
                  <label>怀孕周数</label>
                  <span>{record.vacationData.pregnancyDuration}周</span>
                </div>
              )}
            </div>
          </section>

          {/* 津贴数据 */}
          {record.recordType === 'allowance' && (
            <section className="info-section">
              <div className="section-header">
                <i className="fas fa-money-bill-wave" />
                <h4>津贴数据</h4>
              </div>
              <div className="info-grid">
                {/* 判断是否为发薪城市（深圳） */}
                {record.city === 'SZ' || record.cityName?.includes('深圳') ? (
                  // 发薪城市显示：政府津贴，应享受补贴金额，实发薪资，津贴天数，津贴补差
                  <>
                    {record.allowanceData?.governmentSubsidy && (
                      <div className="info-item">
                        <label>政府津贴</label>
                        <span>{formatCurrency(record.allowanceData.governmentSubsidy)}</span>
                      </div>
                    )}
                    {record.allowanceData?.monthlyAverageSalary && (
                      <div className="info-item">
                        <label>应享受补贴金额</label>
                        <span>{formatCurrency(record.allowanceData.monthlyAverageSalary)}</span>
                      </div>
                    )}
                    {record.allowanceData?.socialInsuranceBase && (
                      <div className="info-item">
                        <label>实发薪资</label>
                        <span>{formatCurrency(record.allowanceData.socialInsuranceBase)}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <label>津贴天数</label>
                      <span>{record.totalDays}天</span>
                    </div>
                    <div className="info-item">
                      <label>津贴补差</label>
                      <span className={record.companyCompensation > 0 ? 'amount-positive' : record.companyCompensation < 0 ? 'amount-negative' : ''}>
                        {formatCurrency(record.companyCompensation)}
                      </span>
                    </div>
                  </>
                ) : (
                  // 不发薪城市显示：政府津贴，应享受补贴金额，津贴天数，返还金额
                  <>
                    {record.allowanceData?.governmentSubsidy && (
                      <div className="info-item">
                        <label>政府津贴</label>
                        <span>{formatCurrency(record.allowanceData.governmentSubsidy)}</span>
                      </div>
                    )}
                    {record.allowanceData?.monthlyAverageSalary && (
                      <div className="info-item">
                        <label>应享受补贴金额</label>
                        <span>{formatCurrency(record.allowanceData.monthlyAverageSalary)}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <label>津贴天数</label>
                      <span>{record.totalDays}天</span>
                    </div>
                    {record.employeeCompensation !== undefined && (
                      <div className="info-item">
                        <label>返还金额</label>
                        <span className={record.employeeCompensation > 0 ? 'amount-positive' : record.employeeCompensation < 0 ? 'amount-negative' : ''}>
                          {formatCurrency(record.employeeCompensation)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          )}

          {/* 计算时间 */}
          <section className="info-section">
            <div className="section-header">
              <i className="fas fa-clock" />
              <h4>计算信息</h4>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <label>计算时间</label>
                <span>{formatDate(record.calculatedAt)}</span>
              </div>
              <div className="info-item">
                <label>记录类型</label>
                <span>{record.recordType === 'allowance' ? '津贴计算' : '产假计算'}</span>
              </div>
            </div>
          </section>
        </div>

        {/* 固定底部 */}
        <div className="dialog-footer">
          <div>
            <button className="btn-primary" onClick={() => {
              // 重新计算逻辑
              if (onRecalculate) {
                onRecalculate(record);
              }
            }}>
              <i className="fas fa-calculator" />
              重新计算
            </button>
          </div>
          {isHrEnabled() && (
            <button className="btn-secondary" onClick={handleSavePDF}>
              <i className="fas fa-file-pdf" />
              保存PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );

}

export default RecordDetailDialog
